import { Suspense } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLiveUpdates } from '../../hooks/useLiveUpdates';
import { useDetectorJobPoller } from '../../hooks/useDetectorJobPoller';
import { usePreferencesStore } from '../../store/preferencesStore';
import { cn } from '../../utils/cn';
import { Sidebar } from './Sidebar';
import { MobileDrawer } from './MobileDrawer';
import { MobileBottomNav } from './MobileBottomNav';
import { Header } from './Header';
import { Spinner } from '../common/Spinner';
import { PageTransition } from '../common/PageTransition';

function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

export function AppLayout() {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const topLevelKey = '/' + pathname.split('/')[1];
  const dashboardLayout = usePreferencesStore((s) => s.dashboardLayout);
  const sidebarExpanded = usePreferencesStore((s) => s.sidebarExpanded);

  // Widen the content container whenever the pinned sidebar isn't fully open:
  // either there's no sidebar at all (Header layout), or it's collapsed to its
  // 64px rail. Only the pinned/expanded Sidebar layout keeps the narrower width.
  const isContentWide = dashboardLayout === 'header' || (dashboardLayout === 'sidebar' && !sidebarExpanded);
  const contentWidthClass = isContentWide ? 'max-w-screen-3xl' : 'max-w-7xl';

  useLiveUpdates();
  useDetectorJobPoller();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 transition-colors duration-200">
      {/* Fixed floating sidebar (desktop ≥ lg) — only in Sidebar layout */}
      {dashboardLayout === 'sidebar' && <Sidebar />}

      {/* Mobile navigation (< lg): slide-in drawer + persistent bottom tab bar,
          unaffected by the desktop-only dashboardLayout preference */}
      <MobileDrawer />
      <MobileBottomNav />

      {/* Main content — offset to clear the collapsed sidebar on desktop only
          (64px + 12px left + 4px gap = 80px); no offset on mobile, and none in
          Header layout since there's no floating sidebar to clear. */}
      <div className={cn(dashboardLayout === 'sidebar' && 'lg:ml-20', 'flex flex-col min-h-screen')}>

        {/* Sticky header in centered container — a single row in both layouts;
            in Header layout, Header itself embeds the primary nav bar on desktop. */}
        <div className="sticky top-0 z-40 bg-gray-50/90 dark:bg-zinc-950/90 backdrop-blur-md border-b border-gray-100 dark:border-zinc-800/50 transition-colors duration-200">
          <div className={cn(contentWidthClass, 'mx-auto')}>
            <Header />
          </div>
        </div>

        {/* Page content in centered container. Extra bottom padding on mobile
            clears the fixed bottom tab bar (~64px + safe-area). */}
        <main className="flex-1">
          <div className={cn(contentWidthClass, 'mx-auto px-4 py-4 sm:px-6 sm:py-6 pb-24 lg:pb-6')}>
            <Suspense fallback={<PageLoader />}>
              <PageTransition keyValue={topLevelKey}>
                <Outlet />
              </PageTransition>
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
